import { Component, inject, Injector, OnDestroy, OnInit, runInInjectionContext, StaticProvider } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { PageHeaderComponent } from '@/components/shared/page-header/page-header.component';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilderComponent } from '@/components/form-builder/form-builder.component';
import { getDacConfigurationUpdateFormObject } from './utils/dac-configuration-edit.form-object';
import { DacRepository } from '@/store/dac-state/dac-state.repository';
import { TranslateService } from '@ngx-translate/core';
import { MultiFormAccordionService } from '@/components/multi-form-accordion/multi-form-accordion.service';
import { DacTransactionalFormAssignmentService } from '../dac-transactional-form.service';
import { BehaviorSubject, forkJoin, of } from 'rxjs';
import { DacSubmissionPiecesService } from '../dac-submission-pieces.service';
import { dacTransactionalFormAssignmentStoreFactory } from '@/store/dac-state/dac-transactional-form-assignment-state/dac-transactional-form-assignment-state.store';
import { DacUpdateConfigDto, DocType, Scenario, TasksStatus } from '@/types';
import { dacDocSubmissionStoreFactory } from '@/store/dac-doc-submission/dac-doc-submission-state.store';
import { DacService } from '../dac.service';
import { DocSubmissionService } from '@/pages/settings/doc-submission/doc-submission.service';
import { getFormBuilderObjectFactory } from '@/components/form-builder/utils/form-builder-factory';
import { DacScenarioService } from '@/pages/settings/dac-scenarios/dac-scenarios.service';
import { SpinnerComponent } from '@/components/shared/spinner/spinner.component';
import { ButtonModule } from 'primeng/button';
import { dacUpdateConfigValidationSchema } from '@/validations/dac/dac.validation';

@Component({
  selector: 'sm-dac-configuration',
  imports: [PageHeaderComponent, FormBuilderComponent, SpinnerComponent, ButtonModule, AsyncPipe],
  templateUrl: './dac-configuration.component.html',
  styleUrls: ['./dac-configuration.component.scss'],
  providers: [{ provide: 'MultiFormAccordionServiceForTransactionalForm', useClass: MultiFormAccordionService }],
})
export class DacConfigurationComponent implements OnInit, OnDestroy {
  dacService = inject(DacService);
  docSubmissionService = inject(DocSubmissionService);
  dacScenarioService = inject(DacScenarioService);
  dacRepository = inject(DacRepository);
  activatedRoute = inject(ActivatedRoute);
  router = inject(Router);
  translateService = inject(TranslateService);
  injector = inject(Injector);

  id = this.activatedRoute.snapshot.paramMap.get('id');
  version = this.activatedRoute.snapshot.paramMap.get('version');

  breadcrumb = [
    { label: 'dac.title', routerLink: [`/dac-create`, this.id?.toString(), this.version?.toString()] as string[] },
    { label: 'dac.dacConfiguration.title' },
  ];

  loading$ = new BehaviorSubject<boolean>(true);
  isUpdatable$ = new BehaviorSubject<boolean>(true);

  // transactional form deps for multiformaccordion
  multiFormAccordionForTransactionalForm = inject<MultiFormAccordionService>('MultiFormAccordionServiceForTransactionalForm' as any);
  dacTransactionalFormAssignmentService = new DacTransactionalFormAssignmentService(this.multiFormAccordionForTransactionalForm);

  multiFormAccordionsForSubmissionPieces: BehaviorSubject<MultiFormAccordionService[]> = new BehaviorSubject<MultiFormAccordionService[]>([]);
  dacSubmissionPiecesServices: BehaviorSubject<DacSubmissionPiecesService[]> = new BehaviorSubject<DacSubmissionPiecesService[]>([]);

  docTypes = new BehaviorSubject<DocType[]>([]);
  scenarios = new BehaviorSubject<Scenario[]>([]);

  ngOnInit(): void {
    this.loadData();
  }

  ngOnDestroy(): void {
    this.multiFormAccordionForTransactionalForm.reset(0);
  }

  formObject = getFormBuilderObjectFactory();

  loadData(): void {
    this.loading$.next(true);
    this.multiFormAccordionForTransactionalForm.formFactory = (id: string) => dacTransactionalFormAssignmentStoreFactory(id);
    this.dacTransactionalFormAssignmentService.initialize();

    forkJoin([
      this.dacService.getDacDetails(Number(this.id)),
      this.docSubmissionService.getAllFolderTypes(),
      this.dacScenarioService.getByDac(Number(this.id), Number(this.version)),
    ]).subscribe({
      next: ([dacDetails, docTypes, dacScenarios]) => {
        this.isUpdatable$.next(!!dacDetails.isUpdatable || dacDetails.dacDocs.status === TasksStatus.BROUILLON);

        this.docTypes.next(docTypes);
        this.scenarios.next(dacScenarios);

        const selectedScenario = dacScenarios.find(scenario => scenario.id === dacDetails.dacDocs.scenarioId);
        this.dacRepository.setNested('dacInfo', dacDetails.dacDocs);
        this.dacRepository.set('scenarioIdSelected', selectedScenario ? { code: selectedScenario.id, name: selectedScenario.nom } : undefined);
        this.dacRepository.setNested('dacUpdateConfigDto', 'scenarioId', dacDetails.dacDocs.scenarioId);

        runInInjectionContext(this.injector, () => {
          const dynamicSubmissionProviders: StaticProvider[] = this.docTypes.getValue().map(item => ({
            provide: `MultiFormAccordionServiceForSubmissionPieces_${item.libelle}`,
            useClass: MultiFormAccordionService,
            deps: [],
          }));

          const injector = Injector.create({
            providers: dynamicSubmissionProviders,
            parent: this.injector,
          });

          this.multiFormAccordionsForSubmissionPieces.next(
            this.docTypes.getValue().map(docType => injector.get(`MultiFormAccordionServiceForSubmissionPieces_${docType.libelle}`))
          );

          this.multiFormAccordionsForSubmissionPieces.getValue().forEach(multiFormAccordionForSubmissionPieces => {
            multiFormAccordionForSubmissionPieces.formFactory = (id: string) => dacDocSubmissionStoreFactory(id);
            multiFormAccordionForSubmissionPieces.reset(0);
            const service = new DacSubmissionPiecesService(multiFormAccordionForSubmissionPieces);
            this.dacSubmissionPiecesServices.next([...this.dacSubmissionPiecesServices.getValue(), service]);
          });
        });

        this.dacTransactionalFormAssignmentService.setTransactionalFormDtos(dacDetails.dacDocs.dynaForms);
        this.dacSubmissionPiecesServices.getValue().forEach((service, index) => {
          const pieces = dacDetails.dacDocs.submissionPieces.filter(sp => sp.docSoumission.typeDossier?.id == this.docTypes.getValue()[index].id);
          service.setSubmissionPiecesDtos(pieces);
        }); //TODO Split responses

        this.formObject = getDacConfigurationUpdateFormObject({
          store: this.dacRepository,
          translate: (key: string, params?: Record<string, any>) => this.translateService.instant(key, params),
          dacId: Number(this.id),
          dacVer: Number(this.version),
          isUpdatable: this.isUpdatable$,
          multiFormAccordionsForSubmissionPieces: this.multiFormAccordionsForSubmissionPieces,
          docTypes: this.docTypes,
          scenarios: this.scenarios,
        });
        this.loading$.next(false);
      },
    });
  }

  save(): void {
    const dacUpdateConfigDto = this.dacRepository.get<DacUpdateConfigDto>('dacUpdateConfigDto');

    const allSubmissionPiecesDtos = this.dacSubmissionPiecesServices.getValue().flatMap(service => service.getAllSubmissionPiecesDtos());

    dacUpdateConfigDto.dacDocSoumissionCreateRequests = allSubmissionPiecesDtos;

    const result = dacUpdateConfigValidationSchema().safeParse(dacUpdateConfigDto);

    if (!result.success) {
      this.dacRepository.setNested('errors', result.error.flatten().fieldErrors);
      return;
    }

    this.loading$.next(true);

    this.dacService.updateDacScenarioConfig(Number(this.id), Number(this.version), dacUpdateConfigDto).subscribe({
      next: () => {
        this.router.navigate(['/dac-create', this.id, this.version]);
      },
      complete: () => this.loading$.next(false),
    });
  }

  reset(): void {
    this.multiFormAccordionForTransactionalForm.reset(0);
    this.multiFormAccordionsForSubmissionPieces.getValue().forEach(s => s.reset(0));
    this.multiFormAccordionsForSubmissionPieces.next([]);
    this.dacSubmissionPiecesServices.next([]);
    this.dacRepository.reset();
    this.formObject = getFormBuilderObjectFactory();
    this.loadData();
  }
}
